(() => {
  const DEBUG = false;

  class CartComplementaryProducts extends HTMLElement {
    constructor() {
      super();

      this.wrapper = document.querySelector('[data-cart-upsell-wrapper]');
      this.container = this.querySelector('[data-products-container]');

      this.maxProducts = parseInt(this.dataset.maxProducts || '4', 10);
      this.imageRatio = (this.dataset.imageRatio || 'adapt').toLowerCase();

      this._refreshTimer = null;
      this._inFlight = false;

      this._onExternalRefresh = this.refresh.bind(this);
    }

    connectedCallback() {
      if (this.wrapper) this.wrapper.style.display = 'none';

      this.refresh();

      document.addEventListener('cart:updated', this._onExternalRefresh);
      document.addEventListener('cart:refresh', this._onExternalRefresh);
      document.addEventListener('cart:upsell-refresh', this._onExternalRefresh);

      this.patchFetchForCartChanges();
    }

    disconnectedCallback() {
      document.removeEventListener('cart:updated', this._onExternalRefresh);
      document.removeEventListener('cart:refresh', this._onExternalRefresh);
      document.removeEventListener('cart:upsell-refresh', this._onExternalRefresh);
    }

    patchFetchForCartChanges() {
      if (window.__cartUpsellFetchPatched) return;
      window.__cartUpsellFetchPatched = true;

      const originalFetch = window.fetch.bind(window);
      window.fetch = async (...args) => {
        const res = await originalFetch(...args);
        try {
          const url = typeof args[0] === 'string' ? args[0] : args[0]?.url || '';
          if (
            url.includes('/cart/add') ||
            url.includes('/cart/change') ||
            url.includes('/cart/update') ||
            url.includes('/cart/clear')
          ) {
            setTimeout(() => {
              document.dispatchEvent(new CustomEvent('cart:upsell-refresh'));
            }, 0);
          }
        } catch {}
        return res;
      };
    }

    refresh() {
      clearTimeout(this._refreshTimer);
      this._refreshTimer = setTimeout(() => this._refreshNow(), 150);
    }

    async _refreshNow() {
      if (!this.container || this._inFlight) return;
      this._inFlight = true;

      try {
        const root = window.Shopify?.routes?.root || '/';
        const cart = await fetch(`${root}cart.js`, {
          credentials: 'same-origin'
        }).then(r => r.json());

        if (!cart?.items?.length) {
          this.container.innerHTML = '';
          this.hide();
          return;
        }

        const handles = await this.fetchComplementaryHandles(cart.items);
        if (!handles.length) {
          this.container.innerHTML = '';
          this.hide();
          return;
        }

        const products = await this.fetchProductsJson(
          handles.slice(0, this.maxProducts)
        );

        if (!products.length) {
          this.container.innerHTML = '';
          this.hide();
          return;
        }

        this.renderCards(products);
        this.show();
      } catch (e) {
        console.error('[Upsell] Error:', e);
        if (!this.container.children.length) this.hide();
      } finally {
        this._inFlight = false;
      }
    }

    show() {
      if (!this.wrapper) return;
      this.wrapper.style.display = 'block';

      // Trigger Dawn scroll animation if enabled
      if (window.ScrollTrigger) {
        window.ScrollTrigger.refresh();
      }
    }

    hide() {
      if (!this.wrapper) return;
      this.wrapper.style.display = 'none';
    }

    async fetchComplementaryHandles(cartItems) {
      const seen = new Set(cartItems.map(i => i.product_id));
      const handles = [];

      for (const item of cartItems) {
        if (handles.length >= this.maxProducts) break;

        const res = await fetch(
          `/recommendations/products.json?product_id=${item.product_id}&intent=complementary&limit=10`,
          { credentials: 'same-origin' }
        );
        if (!res.ok) continue;

        const data = await res.json();
        for (const p of data.products || []) {
          if (handles.length >= this.maxProducts) break;
          if (!p?.id || seen.has(p.id)) continue;
          seen.add(p.id);
          if (p.handle) handles.push(p.handle);
        }
      }

      return handles;
    }

    async fetchProductsJson(handles) {
      const out = [];
      for (const handle of handles) {
        try {
          const res = await fetch(`/products/${handle}.js`, {
            credentials: 'same-origin'
          });
          if (!res.ok) continue;
          out.push(await res.json());
        } catch {}
      }
      return out;
    }

    normalizeUrl(url) {
      if (!url) return '';
      if (url.startsWith('//')) url = `https:${url}`;
      if (url.startsWith('/')) url = `${location.origin}${url}`;
      return url;
    }

    withWidth(url, width = 500) {
      url = this.normalizeUrl(url);
      if (!url) return '';
      if (/[?&]width=\d+/i.test(url)) return url;
      return url.includes('?') ? `${url}&width=${width}` : `${url}?width=${width}`;
    }

    pickImage(product) {
      let img =
        product.featured_image ||
        (Array.isArray(product.images) ? product.images[0] : '');

      if (!img && product.variants?.[0]?.featured_image) {
        const vfi = product.variants[0].featured_image;
        img = typeof vfi === 'string' ? vfi : vfi?.src || '';
      }

      return this.withWidth(img, 500);
    }

    formatMoneyFromCents(cents) {
      const fmt = window.Shopify?.money_format;
      const amount = (Number(cents) / 100).toFixed(2);
      if (fmt && fmt.includes('{{amount}}')) return fmt.replace('{{amount}}', amount);
      return `£${amount}`;
    }

    getAspectRatioStyle() {
      if (this.imageRatio === 'portrait') return 'aspect-ratio: 4 / 5;';
      if (this.imageRatio === 'square') return 'aspect-ratio: 1 / 1;';
      return 'aspect-ratio: 1 / 1;';
    }

    getObjectFit() {
      return this.imageRatio === 'adapt' ? 'contain' : 'cover';
    }

    renderCards(products) {
      const ratioStyle = this.getAspectRatioStyle();
      const fit = this.getObjectFit();

      this.container.innerHTML = products.map(p => {
        const title = p.title || p.handle;
        const img = this.pickImage(p);
        const url = p.url || `/products/${p.handle}`;
        const variant = p.variants.find(v => v.available) || p.variants[0];

        return `
          <li class="grid__item">
            <div class="card-wrapper underline-links-hover">
              <div class="card card--standard">
                <div style="display:flex; flex-direction:column; gap:0.9rem;">

                  <a href="${url}" class="full-unstyled-link">
                    <div style="${ratioStyle} overflow:hidden; margin-bottom:0.75rem;">
                      ${img ? `
                        <img
                          src="${img}"
                          alt="${this.escapeHtml(title)}"
                          loading="lazy"
                          style="width:100%; height:100%; object-fit:${fit}; display:block;"
                        >
                      ` : ''}
                    </div>
                  </a>

                  <div>
                    <h3 class="card__heading h5">
                      <a href="${url}" class="full-unstyled-link" style="color:#fff;">
                        ${this.escapeHtml(title)}
                      </a>
                    </h3>

                    <div class="price">
                      <span class="price-item price-item--regular">
                        ${this.formatMoneyFromCents(p.price)}
                      </span>
                    </div>
                  </div>

                  <button
                    type="button"
                    class="button button--secondary button--full-width"
                    data-upsell-add
                    data-variant-id="${variant.id}"
                    data-product-title="${this.escapeHtml(title)}"
                    data-product-handle="${p.handle}"
                    data-product-price="${p.price}"
                  >
                    Add to cart
                  </button>

                </div>
              </div>
            </div>
          </li>
        `;
      }).join('');

      this.bindAddToCartButtons();
    }

    bindAddToCartButtons() {
      this.container.querySelectorAll('[data-upsell-add]').forEach(btn => {
        btn.addEventListener('click', async () => {
          const variantId = Number(btn.dataset.variantId);
          if (!variantId) return;

          btn.disabled = true;
          btn.textContent = 'Adding…';

          try {
            const res = await fetch('/cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'same-origin',
              body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
            });

            if (!res.ok) throw new Error('Add failed');

            /* ---------- GA4 EVENT ---------- */
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
              event: 'product_upsell_add_to_cart',
              ecommerce: {
                items: [{
                  item_name: btn.dataset.productTitle,
                  item_id: btn.dataset.productHandle,
                  price: Number(btn.dataset.productPrice) / 100,
                  quantity: 1
                }]
              }
            });
            /* -------------------------------- */

            // REQUIRED: hard refresh on cart page
            if (location.pathname === '/cart') {
              setTimeout(() => location.reload(), 250);
            }
          } catch (e) {
            console.error('[Upsell] add to cart failed', e);
            btn.disabled = false;
            btn.textContent = 'Add to cart';
          }
        });
      });
    }

    escapeHtml(text) {
      const div = document.createElement('div');
      div.textContent = text || '';
      return div.innerHTML;
    }
  }

  if (!customElements.get('cart-complementary-products')) {
    customElements.define('cart-complementary-products', CartComplementaryProducts);
  }
})();